import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import DownloadIcon from '@mui/icons-material/Download';
import { keyframes, styled } from '@mui/material/styles';

// Animaciones
const mateBubble = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
`;

const floatingAnimation = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const paperFly = keyframes`
  0% {
    transform: translateX(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateX(100px) rotate(15deg);
    opacity: 0;
  }
`;

// Componentes estilizados
const MateContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '200px',
  height: '200px',
  margin: '0 auto 20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const MateBody = styled('div')(({ theme }) => ({
  width: '120px',
  height: '140px',
  backgroundColor: '#8B4513',
  borderRadius: '50% 50% 40% 40%',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '80px',
    height: '100px',
    backgroundColor: '#654321',
    borderRadius: '40%',
    top: '20px',
    left: '20px',
    opacity: 0.7,
  },
}));

const MateBombilla = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: '6px',
  height: '120px',
  backgroundColor: '#A0522D',
  top: '10px',
  left: '50%',
  transform: 'translateX(-50%) rotate(15deg)',
  zIndex: 2,
  '&::before': {
    content: '""',
    position: 'absolute',
    width: '20px',
    height: '20px',
    backgroundColor: '#A0522D',
    borderRadius: '50%',
    top: '-8px',
    left: '-7px',
  },
}));

const MateBubble = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: '40px',
  height: '40px',
  backgroundColor: '#D2B48C',
  borderRadius: '50%',
  top: '30px',
  right: '40px',
  animation: `${mateBubble} 2s infinite ease-in-out`,
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '30px',
    height: '30px',
    backgroundColor: '#E8D4B6',
    borderRadius: '50%',
    top: '5px',
    left: '5px',
  },
}));

const EmailIconWrapper = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 3,
  animation: `${floatingAnimation} 3s infinite ease-in-out`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const SuccessCheck = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '-10px',
  right: '-10px',
  backgroundColor: '#4CAF50',
  borderRadius: '50%',
  width: '40px',
  height: '40px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: 'white',
  zIndex: 4,
  animation: `${floatingAnimation} 2s infinite ease-in-out`,
}));

const PaperPlane = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '60px',
  right: '80px',
  animation: `${paperFly} 2s infinite`,
  '& svg': {
    fontSize: '24px',
    color: '#be0411',
  },
}));

interface SuccessDialogProps {
  open: boolean;
  onClose: () => void;
  clickupLink?: string;
}

const SuccessDialog: React.FC<SuccessDialogProps> = ({ open, onClose, clickupLink }) => {
  const handleOpenClickupLink = () => {
    if (clickupLink) {
      window.open(clickupLink, '_blank');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'visible',
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: '#be0411', 
          color: 'white', 
          fontWeight: 700, 
          fontSize: '20px',
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          '& .MuiSvgIcon-root': {
            fontSize: '28px'
          }
        }}
      >
        <CheckCircleIcon sx={{ color: 'white' }} />
        ¡Reporte Generado y Enviado!
      </DialogTitle>
      
      <DialogContent sx={{ pt: 4, pb: 2 }}>

        <Typography 
          variant="h6" 
          align="center" 
          sx={{ 
            m: 2, 
            fontWeight: 600,
            color: '#be0411'  
          }}
        >
          Todo listo ✅
        </Typography>
        
        <Typography variant="body1" align="center" sx={{ mb: 3, color: '#555' }}>
          El reporte PDF se ha generado exitosamente y ha sido enviado por correo electrónico.
        </Typography>

        <Box sx={{ 
          backgroundColor: '#f9f9f9', 
          padding: 3, 
          borderRadius: '12px',
          border: '1px solid #e0e0e0',
          mb: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '4px',
            height: '100%',
            backgroundColor: '#be0411',
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <DownloadIcon sx={{ color: '#be0411', mr: 1 }} />
            <Typography variant="subtitle2" sx={{ color: '#be0411', fontWeight: 600 }}>
              El PDF se ha descargado automáticamente
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            • Revisa la carpeta de descargas de tu navegador
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            • El reporte también se envió a los destinatarios por email
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            • Se ha creado una copia en el sistema
          </Typography>
        </Box>

        {clickupLink && (
          <Box sx={{ 
            backgroundColor: '#fff8f8', 
            padding: 3, 
            borderRadius: '12px',
            border: '1px solid #ffd6d6',
            mt: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <OpenInNewIcon sx={{ color: '#be0411', mr: 1 }} />
              <Typography variant="subtitle2" sx={{ color: '#be0411', fontWeight: 600 }}>
                Disponible en ClickUp
              </Typography>
            </Box>
            
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              El documento también se ha cargado en la plataforma:
            </Typography>
            
            <Box sx={{ 
              backgroundColor: 'white', 
              padding: 2, 
              borderRadius: '8px',
              border: '1px dashed #be0411',
              mb: 2
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#be0411', 
                  wordBreak: 'break-all',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  fontFamily: 'monospace'
                }}
              >
                {clickupLink}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1, gap: 2 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            color: '#666',
            borderColor: '#ddd',
            '&:hover': {
              borderColor: '#be0411',
              backgroundColor: 'rgba(190, 4, 17, 0.04)'
            }
          }}
        >
          Cerrar
        </Button>
        
        {clickupLink && (
          <Button 
            onClick={handleOpenClickupLink}
            variant="contained" 
            startIcon={<OpenInNewIcon />}
            sx={{ 
              backgroundColor: '#be0411',
              '&:hover': { 
                backgroundColor: '#a00310',
                boxShadow: '0 4px 12px rgba(190, 4, 17, 0.3)'
              },
              fontWeight: 600,
              padding: '8px 20px'
            }}
          >
            Abrir en ClickUp
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SuccessDialog;